import React, { useEffect, useState, useRef, useCallback } from 'react'; // Added useCallback
import { Container, Form, Button, Alert, Image, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage, auth } from '../../../firebase';
import { collection, addDoc, Timestamp, doc, getDoc, updateDoc, query, where, getDocs as getFirestoreDocs } from 'firebase/firestore'; // Removed writeBatch
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../components/context/AuthContext';
import { useNotification } from '../../../components/ui/Notification';
import Layout from '../../../components/ui/Layout';
import BreadcrumbNavigation from '../../../components/ui/BreadcrumbNavigation';
import { createLogger } from '../../../utils/logger';
import { validateUpcLength } from '../../../utils/upcValidation';

const logger = createLogger('CreateProduct');

const CreateProduct = () => {
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { id: productId } = useParams();
  const isEditMode = !!productId;

  const [displayName, setDisplayName] = useState('');
  const [seoName, setSeoName] = useState('');
  const [upc, setUpc] = useState('');
  const [upcError, setUpcError] = useState('');
  const [upcTouched, setUpcTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [attributes, setAttributes] = useState([{ name: '', value: '' }]);
  const [status, setStatus] = useState('draft');
  const [tagsString, setTagsString] = useState('');
  const [categoryInput, setCategoryInput] = useState(''); // For the category text input
  const [categories, setCategories] = useState([]); // For storing existing categories
  const [files, setFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false); 
  const [pageLoading, setPageLoading] = useState(true); // Start true to load categories & product data

  const [isOtherCategory, setIsOtherCategory] = useState(false); // Add this state to track if "Other" is selected

  const fileInputRef = useRef();

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/admin", label: "Admin Panel" },
    { path: "/admin/products", label: "Products" },
    { label: isEditMode ? "Edit Product" : "Create Product" }
  ];

  // Fetch existing categories
  const fetchCategories = useCallback(async () => {
    try {
      const categoriesSnapshot = await getFirestoreDocs(collection(db, 'Categories'));
      const categoriesList = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(categoriesList);

    } catch (err) {
      logger.error("[CreateProduct] Failed to fetch categories:", err);
      setError(prev => `${prev  } Failed to load categories.`);
    }
  }, []);

  useEffect(() => {
    setPageLoading(true);
    fetchCategories(); // Fetch categories on mount/mode change

    if (isEditMode && productId) {
      const fetchProduct = async () => {
        try {
          const docRef = doc(db, 'Products', productId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setDisplayName(data.display_name || '');
            setSeoName(data.seo_name || '');
            setUpc(data.upc || '');
            setDescription(data.description || '');
            setAttributes(data.attributes && data.attributes.length > 0 ? data.attributes : [{ name: '', value: '' }]);
            setStatus(data.status || 'draft');
            setExistingMedia(data.media || []);
            setTagsString((data.tags && Array.isArray(data.tags)) ? data.tags.join(', ') : '');
            setCategoryInput(data.category || ''); // Populate category input
          } else {
            setError('Product not found.');
          }
        } catch (err) {
          setError(`Failed to load product data. ${  err.message}`);
        } finally {
            // setPageLoading(false) will be called after categories are also fetched if needed
        }
      };
      fetchProduct().finally(() => setPageLoading(false));
    } else {
      // Create mode
      setDisplayName(''); setSeoName(''); setUpc(''); setDescription(''); setAttributes([{ name: '', value: '' }]);
      setStatus('draft'); setFiles([]); setExistingMedia([]); setTagsString(''); setCategoryInput('');
      setPageLoading(false); // Categories are still fetched, then loading is false
    }
  }, [productId, isEditMode, fetchCategories]);

  const handleAttributeChange = (index, field, value) => {
    const newAttributes = [...attributes];
    newAttributes[index][field] = value;
    setAttributes(newAttributes);
  };
  const addAttribute = () => setAttributes([...attributes, { name: '', value: '' }]);
  const removeAttribute = (index) => setAttributes(attributes.filter((_, i) => i !== index));
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate files before setting state
    const validFiles = selectedFiles.filter(file => {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        showNotification({ type: 'warning', title: 'File Too Large', message: `File ${file.name} is too large. Maximum size is 5MB.` });
        return false;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        showNotification({ type: 'warning', title: 'Invalid File Type', message: `File ${file.name} is not an image. Please select image files only.` });
        return false;
      }
      
      return true;
    });
    
    setFiles(validFiles);
  };
  const handleRemoveExistingImage = (index) => setExistingMedia(prev => prev.filter((_, i) => i !== index));
  const handleRemoveNewImage = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

  const generateSeoName = (name) => {
    if (!name || !name.trim()) return '';
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  useEffect(() => {
    if (displayName && !seoName) {
      setSeoName(generateSeoName(displayName));
    }
  }, [displayName]); // Only auto-fill when displayName changes and seoName is empty

  const generateKeywords = (name, desc, category, tagsArray, upcValue) => {
    const nameWords = name ? name.toLowerCase().split(/\s+/).filter(Boolean) : [];
    const descWords = desc ? desc.toLowerCase().split(/\s+/).filter(Boolean).slice(0, 10) : [];
    const categoryWords = category ? category.toLowerCase().split(/\s+/).filter(Boolean) : [];
    const tagKeywords = tagsArray ? tagsArray.map(tag => tag.toLowerCase().trim()).filter(Boolean) : [];
    const combinedName = name ? name.toLowerCase().replace(/\s+/g, '') : '';
    const upcKeywords = upcValue ? [upcValue.trim()] : [];
    const keywords = new Set([
      ...nameWords, ...descWords, ...categoryWords, ...tagKeywords,
      ...upcKeywords,
      ...(combinedName ? [combinedName] : [])
    ]);
    return Array.from(keywords);
  };

  const uploadFiles = async () => {
    const uploadedMediaObjects = [];
    const uniqueIdPart = productId || Date.now();
    
    for (const file of files) {
      try {
        
        // Better filename sanitization
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const timestamp = Date.now();
        const gcsPath = `images/products/${uniqueIdPart}/${timestamp}_${sanitizedName}`;
        
        // Ensure we have authentication before upload
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('User not authenticated');
        }
        
        // Refresh the user's token to ensure it's valid
        try {
          await currentUser.getIdToken(true);
 
        } catch (tokenError) {
          logger.error('Token refresh failed:', tokenError);
          throw new Error('Authentication token refresh failed');
        }
        
                                         const fileRef = ref(storage, gcsPath);
             const uploadTask = uploadBytesResumable(fileRef, file);
        
        await new Promise((resolve, _reject) => {
          uploadTask.on('state_changed',
                         (_snapshot) => { 
               // Progress tracking - removed unused variable
             }, 
            (error) => { 
              logger.error('Upload error for file:', file.name, error); 
              logger.error('Error details:', {
                code: error.code,
                message: error.message,
                serverResponse: error.serverResponse
              });
              
              // Fallback: use local public fallback image
              uploadedMediaObjects.push({
                name: 'fallback-product.svg',
                url: '/fallback-product.svg',
                path: 'public/fallback-product.svg',
                type: 'image/svg+xml',
                size: 0,
                created_at: Timestamp.fromDate(new Date())
              });
              resolve(); // Do not reject, continue with fallback
            },
            async () => {
              try {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  uploadedMediaObjects.push({ 
                    name: file.name, 
                    url: downloadURL, 
                    path: gcsPath, 
                    type: file.type, 
                    size: file.size, 
                    created_at: Timestamp.fromDate(new Date()) 
                });
                
                resolve();
              } catch (urlError) {
                logger.error('Error getting download URL for file:', file.name, urlError);
                              // Fallback: use local public fallback image
              uploadedMediaObjects.push({
                name: 'fallback-product.svg',
                url: '/fallback-product.svg',
                path: 'public/fallback-product.svg',
                type: 'image/svg+xml',
                size: 0,
                created_at: Timestamp.fromDate(new Date())
              });
                resolve();
              }
            }
          );
        });
      } catch (error) {
        logger.error('Unexpected error during upload:', error);
        // Fallback for unexpected errors
        uploadedMediaObjects.push({
          name: 'fallback-product.svg',
          url: '/fallback-product.svg',
          path: 'public/fallback-product.svg',
          type: 'image/svg+xml',
          size: 0,
          created_at: Timestamp.fromDate(new Date())
        });
      }
    }
    return uploadedMediaObjects;
  };

  const handleUpcChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    setUpc(digitsOnly);
    
    // Validate on change if field has been touched
    if (upcTouched) {
      const validation = validateUpcLength(digitsOnly);
      setUpcError(validation.error || '');
    }
  };

  const handleUpcBlur = () => {
    setUpcTouched(true);
    const validation = validateUpcLength(upc);
    setUpcError(validation.error || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!displayName || !categoryInput) { setError('Product name and category are required.'); return; }
    
    // Validate UPC before submission
    const upcValidation = validateUpcLength(upc);
    if (!upcValidation.valid) {
      setUpcError(upcValidation.error);
      setUpcTouched(true);
      setError('Please fix validation errors before submitting.');
      return;
    }
    
    // Check authentication before upload
    if (!currentUser) {
      setError('You must be logged in to upload files.');
      return;
    }
    
    setLoading(true); setError(''); setSuccess('');
    try {
      const newUploadedMedia = await uploadFiles();
      const finalMedia = [...existingMedia, ...newUploadedMedia];
      const tagsArray = tagsString.split(',').map(tag => tag.trim().toLowerCase()).filter(tag => tag !== '');
      const finalCategory = categoryInput.trim().toLowerCase();
      const trimmedUpc = upc.trim();
      const keywords = generateKeywords(displayName, description, finalCategory, tagsArray, trimmedUpc);

      // Add new category to Categories collection if it doesn't exist
      if (finalCategory && !categories.some(cat => cat.name.toLowerCase() === finalCategory)) {
        const categoryQuery = query(collection(db, 'Categories'), where("name_lowercase", "==", finalCategory));
        const existingCategorySnap = await getFirestoreDocs(categoryQuery);
        if (existingCategorySnap.empty) {
            await addDoc(collection(db, 'Categories'), {
                name: categoryInput.trim(), // Store with original casing for display
                name_lowercase: finalCategory,
                createdAt: Timestamp.fromDate(new Date())
            });
            
            fetchCategories(); // Re-fetch categories to update datalist
        }
      }

      const finalSeoName = (seoName || generateSeoName(displayName) || '').trim();
      let uniqueSeoName = finalSeoName;
      if (uniqueSeoName) {
        const seoQuery = query(
          collection(db, 'Products'),
          where('seo_name', '==', uniqueSeoName)
        );
        const existingSeo = await getFirestoreDocs(seoQuery);
        const excludeId = isEditMode ? productId : null;
        const conflicts = existingSeo.docs.filter(d => d.id !== excludeId);
        if (conflicts.length > 0) {
          let suffix = 2;
          while (true) {
            const candidate = `${uniqueSeoName}-${suffix}`;
            const candQuery = query(collection(db, 'Products'), where('seo_name', '==', candidate));
            const candSnap = await getFirestoreDocs(candQuery);
            const candConflicts = candSnap.docs.filter(d => d.id !== excludeId);
            if (candConflicts.length === 0) {
              uniqueSeoName = candidate;
              break;
            }
            suffix++;
          }
        }
      }

      const productData = {
        display_name: displayName, description, category: categoryInput.trim(), // Save original casing category
        upc: trimmedUpc || null,
        seo_name: uniqueSeoName || null,
        attributes: attributes.filter(attr => attr.name && attr.value),
        status, media: finalMedia, tags: tagsArray, search_keywords: keywords,
        updated_at: Timestamp.fromDate(new Date()),
      };

      if (isEditMode) {
        const docRef = doc(db, 'Products', productId);
        await updateDoc(docRef, productData);
        setSuccess('Product updated successfully!');
      } else {
        productData.created_at = Timestamp.fromDate(new Date());
        productData.created_by = { uid: currentUser.uid, email: currentUser.email };
        
        await addDoc(collection(db, 'Products'), productData);
        setSuccess('Product created successfully!');
        setDisplayName(''); setSeoName(''); setUpc(''); setUpcError(''); setUpcTouched(false); setDescription(''); setAttributes([{ name: '', value: '' }]); 
        setStatus('draft'); setFiles([]); setTagsString(''); setCategoryInput('');
        if(fileInputRef.current) fileInputRef.current.value = '';
      }
      setTimeout(() => navigate('/admin/products'), 1500);
    } catch (err) {
      setError(`Operation failed: ${  err.message}`); logger.error(err);
    }
    setLoading(false);
  };

  const handleGoBack = () => navigate(-1);

  // Update this handler to manage dropdown/text input switch
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "__other__") {
      setIsOtherCategory(true);
      setCategoryInput('');
    } else {
      setIsOtherCategory(false);
      setCategoryInput(value);
    }
  };

  if (pageLoading) { // Simplified page loading check
    return <Layout><Container className="text-center mt-5"><Spinner animation="border" /> <p>Loading form data...</p></Container></Layout>;
  }

  return (
    <Layout>
      <Container className="mt-3 px-3">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={handleGoBack} className="mb-2">&larr; Go Back</Button>
            </Col>
            <Col><BreadcrumbNavigation items={breadcrumbItems} /></Col>
        </Row>
        <h1>{isEditMode ? 'Edit Product' : 'Create Product'}</h1>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="productDisplayName">
            <Form.Label className="fw-semibold">Product Name</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Enter product name" 
              value={displayName} 
              onChange={(e) => setDisplayName(e.target.value)} 
              required 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="productSeoName">
            <Form.Label className="fw-semibold">SEO-friendly URL name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., organic-milk (auto-generated from name)"
              value={seoName}
              onChange={(e) => setSeoName(e.target.value)}
              className="w-100"
              style={{
                fontSize: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
            <Form.Text className="text-muted small">
              URL-friendly name for SEO. Use lowercase letters, numbers, and hyphens only. Auto-generated from product name when empty.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="productUpc">
            <Form.Label className="fw-semibold">UPC (Universal Product Code)</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., 012345678905"
              value={upc}
              onChange={(e) => handleUpcChange(e.target.value)}
              onBlur={handleUpcBlur}
              isInvalid={upcTouched && !!upcError}
              className="w-100"
              maxLength={14}
              style={{
                fontSize: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: upcTouched && upcError ? '2px solid #dc3545' : '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
            {upcTouched && upcError && (
              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                {upcError}
              </Form.Control.Feedback>
            )}
            {(!upcTouched || !upcError) && (
              <Form.Text className="text-muted small">
                Optional. Enter the barcode number (12, 13, or 14 digits).
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="productCategory">
            <Form.Label className="fw-semibold">Category</Form.Label>
            <Form.Select
              value={isOtherCategory ? "__other__" : categoryInput}
              onChange={handleCategoryChange}
              required
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
              <option value="__other__">Other (type new...)</option>
            </Form.Select>
            {isOtherCategory && (
              <Form.Control
                className="mt-2 w-100"
                type="text"
                placeholder="Type new category"
                value={categoryInput}
                onChange={e => setCategoryInput(e.target.value)}
                required
                style={{ 
                  fontSize: '16px', // Prevents zoom on iOS
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #dee2e6',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              />
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="productDescription">
            <Form.Label className="fw-semibold">Description</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              placeholder="Product description" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="productTags">
            <Form.Label className="fw-semibold">Tags (comma-separated)</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="e.g., organic, best-seller" 
              value={tagsString} 
              onChange={(e) => setTagsString(e.target.value)} 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>
          {attributes.map((attr, index) => (
            <Row key={index} className="mb-2 align-items-end">
              <Col xs={12} md={5} className="mb-2 mb-md-0">
                <Form.Group controlId={`attrName-${index}`}>
                  {index === 0 && <Form.Label className="fw-semibold">Attribute Name</Form.Label>}
                  <Form.Control 
                    type="text" 
                    placeholder="e.g., Color" 
                    value={attr.name} 
                    onChange={(e) => handleAttributeChange(index, 'name', e.target.value)} 
                    className="w-100"
                    style={{ 
                      fontSize: '16px', // Prevents zoom on iOS
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #dee2e6',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={5} className="mb-2 mb-md-0">
                <Form.Group controlId={`attrValue-${index}`}>
                  {index === 0 && <Form.Label className="fw-semibold">Attribute Value</Form.Label>}
                  <Form.Control 
                    type="text" 
                    placeholder="e.g., Red" 
                    value={attr.value} 
                    onChange={(e) => handleAttributeChange(index, 'value', e.target.value)} 
                    className="w-100"
                    style={{ 
                      fontSize: '16px', // Prevents zoom on iOS
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #dee2e6',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={2} className="d-flex align-items-center">
                {attributes.length > 1 && (
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={() => removeAttribute(index)} 
                    className="w-100 w-md-auto"
                    style={{ 
                      minHeight: '44px', // Better touch target
                      fontSize: '14px',
                      padding: '8px 16px'
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Col>
            </Row>))}
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={addAttribute} 
            className="mb-3 w-100 w-md-auto"
            style={{ 
              minHeight: '44px', // Better touch target
              fontSize: '14px',
              padding: '8px 16px'
            }}
          >
            + Add Attribute
          </Button>
          <Form.Group className="mb-3" controlId="productStatus">
            <Form.Label className="fw-semibold">Status</Form.Label>
            <Form.Select 
              value={status} 
              onChange={(e) => setStatus(e.target.value)}
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="productFormFiles" className="mb-3">
            <Form.Label className="fw-semibold">Upload Images</Form.Label>
            <Form.Control 
              type="file" 
              multiple 
              onChange={handleFileChange} 
              ref={fileInputRef} 
              accept="image/*" 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>
          {isEditMode && existingMedia.length > 0 && (<div className="mb-3"><p className="fw-bold">Current Images:</p><div className="d-flex flex-wrap">{existingMedia.map((media, index) => (<div key={`existing-${index}`} className="me-2 mb-2 border p-1 text-center"><Image src={media.url} alt={media.name || `Image ${index+1}`} thumbnail style={{maxWidth: '100px', maxHeight: '100px', objectFit: 'contain'}} /><Button variant="outline-danger" size="sm" className="mt-1 d-block mx-auto" onClick={() => handleRemoveExistingImage(index)}>Remove</Button></div>))}</div></div>)}
          {files.length > 0 && (<div className="mb-3"><p className="fw-bold">New Images to Upload:</p><div className="d-flex flex-wrap">{files.map((file, index) => (<div key={`new-${index}`} className="me-2 mb-2 border p-1 text-center"><Image src={URL.createObjectURL(file)} alt={file.name} thumbnail style={{maxWidth: '100px', maxHeight: '100px', objectFit: 'contain'}} /><Button variant="outline-danger" size="sm" className="mt-1 d-block mx-auto" onClick={() => handleRemoveNewImage(index)}>Remove</Button></div>))}</div></div>)}
          

          
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || pageLoading}
            className="w-100 w-md-auto"
            size="lg"
            style={{ 
              minHeight: '48px', // Better touch target
              fontSize: '16px',
              padding: '12px 24px'
            }}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Processing...
              </>
            ) : (
              isEditMode ? 'Update Product' : 'Create Product'
            )}
          </Button>
        </Form>
      </Container>
    </Layout>
  );
};

export default CreateProduct;
